import React, { useState, useEffect } from "react";
import { 
  Box, 
  Typography, 
  Paper, 
  Avatar,
  Stack,
  Tabs,
  Tab,
  Chip,
  Skeleton
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { POST } from "api/index";
import ScoreIcon from 'assets/icons/scoreIcon.png';
import FlameIcon from 'assets/icons/flame.png';
import { useTranslation } from "react-i18next";


// Responsive ranking cards with better color differentiation
const RankingCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'rank'
})<{ rank: number }>(({ theme, rank }) => ({
  padding: theme.spacing(1.5),
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  cursor: 'pointer',
  minHeight: 60,
  
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2),
    gap: theme.spacing(2),
    borderRadius: 16,
    marginBottom: theme.spacing(1.5),
    minHeight: 70,
  },
  
  // Better color differentiation
  ...(rank === 0 && {
    background: 'linear-gradient(135deg, #FFD700 0%, #FFA000 100%)',
    boxShadow: '0 4px 15px rgba(255, 215, 0, 0.3)',
    [theme.breakpoints.up('sm')]: {
      boxShadow: '0 8px 25px rgba(255, 215, 0, 0.4)',
    }
  }),
  ...(rank === 1 && {
    background: 'linear-gradient(135deg, #E8E8E8 0%, #BDBDBD 100%)',
    color: '#333',
    boxShadow: '0 4px 15px rgba(189, 189, 189, 0.3)',
    [theme.breakpoints.up('sm')]: {
      boxShadow: '0 8px 25px rgba(189, 189, 189, 0.4)',
    }
  }),
  ...(rank === 2 && {
    background: 'linear-gradient(135deg, #D4A574 0%, #CD7F32 100%)',
    boxShadow: '0 4px 15px rgba(212, 165, 116, 0.3)',
    [theme.breakpoints.up('sm')]: {
      boxShadow: '0 8px 25px rgba(212, 165, 116, 0.4)',
    }
  }),
  ...(rank > 2 && {
    background: theme.palette.primary.main, // Much darker than silver
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    border: `1px solid ${theme.palette.grey[500]}`,
    [theme.breakpoints.up('sm')]: {
      boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
    }
  }),
  
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: rank < 3 
      ? '0 6px 20px rgba(0,0,0,0.15)' 
      : '0 4px 15px rgba(0,0,0,0.25)',
    [theme.breakpoints.up('sm')]: {
      transform: 'translateY(-2px)',
      boxShadow: rank < 3 
        ? '0 12px 35px rgba(0,0,0,0.2)' 
        : '0 8px 25px rgba(0,0,0,0.3)'
    }
  }
}));

// Responsive rank badge
const RankBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'rank'
})<{ rank: number }>(({ theme, rank }) => ({
  minWidth: 36,
  height: 36,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 'bold',
  fontSize: '0.9rem',
  flexShrink: 0,
  
  [theme.breakpoints.up('sm')]: {
    minWidth: 48,
    height: 48,
    fontSize: '1.1rem',
  },
  
  ...(rank >= 3 && {
  })
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const MobileRankings: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [streakRanking, setStreakRanking] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const token = window.localStorage.getItem("currentUserToken");
  const { t } = useTranslation();
  useEffect(() => {
    const fetchRankings = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const body = new URLSearchParams();
        body.append("userToken", token);

        const [leaderResponse, streakResponse] = await Promise.all([
          POST("getTopUsers", body),
          POST("getTopStreaks", body)
        ]);

        if (leaderResponse.status === 200) {
          setLeaderboard(leaderResponse.top_users || []);
        }
        if (streakResponse.status === 200) {
          setStreakRanking(streakResponse.top_streaks || []);
        }
      } catch (error) {
        console.error("Error fetching rankings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRankings();
  }, [token]);

  const formatValue = (value: number): string => {
    return value.toLocaleString('de-DE');
  };

  const getRankEmoji = (rank: number) => {
    switch(rank) {
      case 0: return "🥇";
      case 1: return "🥈";  
      case 2: return "🥉";
      default: return `${rank + 1}`;
    }
  };

  const renderRankingList = (users: any[], type: 'points' | 'streak') => {
    if (loading) {
      return (
        <Stack spacing={1}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Paper key={i} sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="circular" width={36} height={36} />
                <Skeleton variant="circular" width={36} height={36} />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton variant="text" width="40%" height={16} />
                </Box>
                <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} />
              </Box>
            </Paper>
          ))}
        </Stack>
      );
    }

    if (users.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            {t("skillboard.rankings.noRankingAvailable")}
          </Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={{ xs: 1, sm: 1.5 }}>
        {users.slice(0, 8).map((user, index) => (
          <RankingCard key={index} rank={index}>
            <RankBadge rank={index}>
              {getRankEmoji(index)}
            </RankBadge>
            
            <Avatar 
              sx={{ 
                width: { xs: 32, sm: 44 }, 
                height: { xs: 32, sm: 44 },
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                flexShrink: 0
              }} 
            />
            
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography 
                variant="body2" 
                fontWeight="bold" 
                noWrap
                sx={{ 
                  fontSize: { xs: '0.85rem', sm: '1rem' },
                  lineHeight: 1.2,
                  mb: 0.5
                  
                }}
              >
                {user.username || `${user.firstName} ${user.lastName.charAt(0)}.`}
              </Typography>
            </Box>
            
            {/* Icon and Value Display */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
              py: 0.5,
              minWidth: { xs: '60px', sm: '80px' },
              justifyContent: 'center'
            }}>
              <img 
                src={type === 'points' ? ScoreIcon : FlameIcon} 
                alt={type === 'points' ? 'Score' : 'Flame'} 
                style={{ 
                  width: 16, 
                  height: 16,
                }} 
              />
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  color: 'inherit'
                }}
              >
                {type === 'points' 
                  ? formatValue(Number(user.total_points))
                  : user.streak
                }
              </Typography>
            </Box>
          </RankingCard>
        ))}
      </Stack>
    );
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography 
        variant="h5" 
        fontWeight="bold" 
        sx={{ 
          mb: 2.5, 
          color: '#1a1a1a',
          fontSize: { xs: '1.4rem', sm: '1.6rem' },
        }}
      >
        {t("skillboard.rankings.title")}
      </Typography>

      <Paper sx={{ 
        borderRadius: { xs: 2, sm: 3 }, 
        overflow: 'hidden', 
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        mx: { xs: 1, sm: 0 }
      }}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          variant="fullWidth"
          sx={{
            bgcolor: 'grey.50',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: { xs: '0.85rem', sm: '1rem' },
              py: { xs: 1.5, sm: 2 },
              minHeight: { xs: 40, sm: 48 }
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          <Tab label={`🪙 ${t("skillboard.rankings.learningPointsTab")}`} />
          <Tab label={`🔥 ${t("skillboard.rankings.streakTab")}`} />
        </Tabs>

        <Box sx={{ 
          p: { xs: 1.5, sm: 2.5 }, 
          minHeight: { xs: 300, sm: 400 }
        }}>
          <TabPanel value={tabValue} index={0}>
            {renderRankingList(leaderboard, 'points')}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {renderRankingList(streakRanking, 'streak')}
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
};

export default MobileRankings;